import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from './config';
import socket from '../socket/socket';
import '../css/AdminMessageInbox.css';

const clampPosition = (x, y, width, height) => ({
  x: Math.max(8, Math.min(window.innerWidth - width - 8, x)),
  y: Math.max(8, Math.min(window.innerHeight - height - 8, y))
});

function AdminMessageInbox({ groupId }) {
  const [alerts, setAlerts] = useState({ matchRequests: [], waitAlerts: [] });
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [buttonPosition, setButtonPosition] = useState(null);
  const [panelPosition, setPanelPosition] = useState(null);
  const dragRef = useRef(null);
  const movedRef = useRef(false);

  const loadAlerts = useCallback(async () => {
    if (!groupId) return;
    try {
      const response = await fetch(`${API_BASE}/api/match-planner/${Number(groupId)}/admin-alerts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'โหลดข้อความไม่สำเร็จ');
      setAlerts({ matchRequests: result.matchRequests || [], waitAlerts: result.waitAlerts || [] });
    } catch (error) {
      console.error('LOAD ADMIN MESSAGE INBOX ERROR:', error);
    }
  }, [groupId]);

  useEffect(() => {
    loadAlerts();
    const refresh = payload => {
      if ((!payload?.groupId || Number(payload.groupId) === Number(groupId)) && String(payload?.type || '').startsWith('WAIT_ALERT')) {
        loadAlerts();
        if (payload?.type === 'WAIT_ALERT') setOpen(true);
      }
    };
    socket.on('admin-alert-updated', refresh);
    const timer = window.setInterval(loadAlerts, 15000);
    return () => {
      socket.off('admin-alert-updated', refresh);
      window.clearInterval(timer);
    };
  }, [groupId, loadAlerts]);

  const startDrag = (event, kind) => {
    if (kind === 'panel' && (event.target.closest('button') || !event.target.closest('header'))) return;
    const rect = event.currentTarget.getBoundingClientRect();
    movedRef.current = false;
    dragRef.current = { kind, pointerX: event.clientX, pointerY: event.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveDrag = event => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.pointerX;
    const dy = event.clientY - drag.pointerY;
    if (Math.abs(dx) + Math.abs(dy) > 5) movedRef.current = true;
    const next = clampPosition(drag.left + dx, drag.top + dy, drag.width, drag.height);
    if (drag.kind === 'button') setButtonPosition(next);
    else setPanelPosition(next);
  };

  const stopDrag = event => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const resolveAlert = async id => {
    setBusyId(Number(id));
    try {
      const response = await fetch(`${API_BASE}/api/match-planner/${Number(groupId)}/wait-alerts/${Number(id)}/resolve`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'บันทึกรับทราบไม่สำเร็จ');
      await loadAlerts();
    } catch (error) { window.alert(error.message); }
    finally { setBusyId(null); }
  };

  const count = alerts.waitAlerts.length;

  return <>
    <button
      type="button"
      className="admin-message-float"
      style={buttonPosition ? { left: buttonPosition.x, top: buttonPosition.y, right: 'auto' } : undefined}
      onPointerDown={event => startDrag(event, 'button')}
      onPointerMove={moveDrag}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
      onClick={() => {
        if (movedRef.current) { movedRef.current = false; return; }
        setOpen(value => !value);
      }}
      title="กดเพื่อเปิด • ลากเพื่อย้ายตำแหน่ง"
    >
      <i aria-hidden="true">🔔</i><span>กล่องข้อความ</span>{count > 0 && <b>{count}</b>}
    </button>

    {open && <aside
      className="admin-message-panel"
      style={panelPosition ? { left: panelPosition.x, top: panelPosition.y, right: 'auto' } : undefined}
      onPointerDown={event => startDrag(event, 'panel')}
      onPointerMove={moveDrag}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
    >
      <header><div><strong>💬 กล่องข้อความผู้เล่น</strong><small>{count} รายการ • ลากหัวกล่องเพื่อย้าย</small></div><button type="button" onClick={() => setOpen(false)}>×</button></header>
      <section>
        {count === 0 && <p>ยังไม่มีข้อความใหม่</p>}
        {alerts.waitAlerts.map(item => <article className="is-wait" key={`wait-${item.id}`}><div><strong>⏰ {item.requester_name}</strong><span>“{item.note || 'แจ้งว่ารอนาน'}”</span></div><button type="button" disabled={busyId === Number(item.id)} onClick={() => resolveAlert(item.id)}>{busyId === Number(item.id) ? 'กำลังบันทึก…' : '✓ รับทราบ'}</button></article>)}
      </section>
    </aside>}
  </>;
}

export default AdminMessageInbox;
